import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth";
import { localizedError } from "../lib/localized-error";
import { Icon } from "./Icon";

export function AuthDialog() {
  const { t, i18n } = useTranslation();
  const { api, closeAuth, isAuthOpen, setSession } = useAuth();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [email, setEmail] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isAuthOpen && !dialog.open) dialog.showModal();
    if (!isAuthOpen && dialog.open) dialog.close();
  }, [isAuthOpen]);

  async function sendCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await api.startEmailLogin(email, i18n.language);
      setChallengeId(response.challenge_id);
    } catch (reason) {
      setError(localizedError(reason, t));
    } finally {
      setBusy(false);
    }
  }

  async function verify(event: FormEvent) {
    event.preventDefault();
    if (!challengeId) return;
    setBusy(true);
    setError(null);
    try {
      setSession(await api.verifyEmailLogin(email, challengeId, code, i18n.language));
      setCode("");
      setChallengeId(null);
    } catch (reason) {
      setError(localizedError(reason, t));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setChallengeId(null);
    setCode("");
    setError(null);
  }

  return (
    <dialog ref={dialogRef} className="auth-dialog" onClose={closeAuth} onCancel={closeAuth}>
      <button type="button" className="icon-button dialog-close" aria-label={t("auth.close")} onClick={closeAuth}><Icon name="close" /></button>
      <div className="auth-mark"><Icon name="wave" /></div>
      <h2>{t("auth.title")}</h2>
      <p>{t("auth.subtitle")}</p>
      {!challengeId ? (
        <form onSubmit={sendCode}>
          <label><span>{t("auth.email")}</span><input type="email" required autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder={t("auth.emailPlaceholder")} /></label>
          <button className="button button--primary button--wide" disabled={busy}>{busy ? t("common.loading") : t("auth.send")}</button>
        </form>
      ) : (
        <form onSubmit={verify}>
          <div className="auth-sent"><Icon name="check" /><span>{t("auth.sent", { email })}</span></div>
          <label><span>{t("auth.code")}</span><input className="code-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))} /></label>
          <button className="button button--primary button--wide" disabled={busy || code.length !== 6}>{busy ? t("common.loading") : t("auth.verify")}</button>
          <button type="button" className="text-button" onClick={reset}>{t("auth.back")}</button>
        </form>
      )}
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="auth-divider"><span>{t("auth.divider")}</span></div>
      <div className="oauth-row">
        <a className="button button--ghost" href={`/api/v1/auth/oauth/google/start?redirect_path=${encodeURIComponent(window.location.pathname)}`}>G&nbsp; {t("auth.google")}</a>
        <a className="button button--ghost" href={`/api/v1/auth/oauth/github/start?redirect_path=${encodeURIComponent(window.location.pathname)}`}>⌘&nbsp; {t("auth.github")}</a>
      </div>
    </dialog>
  );
}
